import min from 'lodash/min';
import forEach from 'lodash/forEach';
import findLast from 'lodash/findLast';
import uniq from 'lodash/uniq';
import { Action } from 'rete-history-plugin';
import { pasteDialogue, setDialoguePayload } from './dialogue';
import { pasteMedia, setMediaPayload } from './media';
import { pasteCustomStates, setCustomStatePayload } from './customStates';

const EXPECTED_CLIPBOARD_TAG = 'vrpatients::caseLogic'

export function copyNodesToClipboard(editor, caseConf) {
  const selectedNodes = editor.selected.list;

  // Create base payload
  const payload = {
    tag: EXPECTED_CLIPBOARD_TAG,
    editorId: editor.id,
    caseId: caseConf.id,
    leafNodes: getLeafNodes(selectedNodes),
    groups: getGroups(selectedNodes, caseConf),
  };

  setDialoguePayload(payload, caseConf);
  setMediaPayload(payload, caseConf);
  setCustomStatePayload(payload, caseConf);

  navigator.clipboard.writeText(JSON.stringify(payload));
}

function getLeafNodes(nodes) {
  // Return nodes that are not groups, and not a child of a group
  let allChildIds = [];
  const groupNodes = nodes.filter(n => n.name === 'Group');
  for (const groupNode of groupNodes) {
    allChildIds = allChildIds.concat(groupNode.data.childIds);
  }
  return nodes.filter(n => n.name !== 'Group' && !allChildIds.includes(n.id))
}

function getGroups(nodes, caseConf) {
  // Return list of groups where a group is { node, children }
  const groups = nodes.filter(n => n.name === 'Group').map(n => ({ node: n, children: [] }))
  
  // Add group children to payload
  for (const group of groups) {
    const uniqueIds = uniq(group.node.data.childIds)
    uniqueIds.forEach(childId => {
      group.children.push(caseConf.caseLogicDescription.nodes[childId]);
    });
  } 
  return groups;
}

export async function pasteNodesFromClipboard(editor, caseConf, mouse, isFromRedo = false) {
  let payload;
  try {
    const clipboardText = await navigator.clipboard.readText();
    payload = JSON.parse(clipboardText);
  } catch(e) {
    return;
  }

  // Check that payload has expected tag, and matches the case logic version
  if (!payload || payload.tag !== EXPECTED_CLIPBOARD_TAG) {
    return;
  }

  // Copy questions/media first so we can update the case logic ids
  const updates = {};
  if (caseConf.id !== payload.caseId) {
    if (pasteDialogue(caseConf, payload)) {
      updates.dialogue = caseConf.dialogue;
    }
  
    if (pasteMedia(caseConf, payload)) {
      updates._labAssets = caseConf._labAssets;
    }

    pasteCustomStates(caseConf, payload);    
  }

  // Get top/left most coordinates of source nodes
  const bbox = topLeft(payload);

  let newMap; // { sourceId: newNode }

  // Paste leaf nodes
  newMap = await pasteNodes(editor, mouse, bbox, payload.leafNodes);
  // Paste group nodes
  for (const group of payload.groups) {
    // Paste group children
    const childMap = await pasteNodes(editor, mouse, bbox, group.children);
    group.node.data.childIds = Object.values(childMap).map(node => node.id); // update for target node ids
    newMap = {
      ...newMap,
      ...childMap
    };

    // Paste group
    const newGroupNode = await createNewNode(editor, mouse, bbox, group.node);
    editor.addNode(newGroupNode);
    newMap[group.node.id] = newGroupNode;
  }

  // Create connctions
  const connections = getConnectionsToPaste(payload);
  connections.forEach(conn => {
    const fromNode = newMap[conn.from];    
    const toNode = newMap[conn.to];
    editor.connect(fromNode.outputs.get(conn.output), toNode.inputs.get('inputs'));
  });

  forEach(newMap, newNode => {
    const nodeView = editor.view.nodes.get(newNode);
    nodeView.trigger('selectnode', { node: newNode, accumulate: true });
  })
  // We don't want to add a paste action to the history
  // if it is being redone as Rete will already add one
  // from the redo
  if (!isFromRedo) {
    editor.trigger('addhistory', new PasteAction(editor, payload, mouse));
  }
  return updates;
}

async function pasteNodes(editor, mouse, bbox, nodes) {
  let newMap = {};
  for (const oldNode of nodes) {
    const newNode = await createNewNode(editor, mouse, bbox, oldNode);
    editor.addNode(newNode);
    newMap[oldNode.id] = newNode;
  }
  return newMap;
}

async function createNewNode(editor, mouse, bbox, node) {
  const component = editor.getComponent(node.name);       
  const newNode = await component.createNode(node.data);
  newNode.position = [
    mouse.x + (node.position[0] - bbox.left),
    mouse.y + (node.position[1] - bbox.top),
  ];
  return newNode;
}

function topLeft(payload) {
  // Find top/left of bounding box of all nodes in paste payload
  // Don't need to look at group children since they're inside the group
  const allNodes = payload.leafNodes.concat(payload.groups.map(g => g.node));
  return {
    left: min(allNodes.map(node => node.position[0])),
    top: min(allNodes.map(node => node.position[1]))
  };
}

function getConnectionsToPaste(payload) {
  // Get list of connections that will exist in pasted selection
  // Node ids are for the source nodes
  let allLeafNodes = payload.leafNodes;
  for (const group of payload.groups) {
    allLeafNodes = allLeafNodes.concat(group.children);
  }
  let connections = [];
  for (const sourceNode of allLeafNodes) {
    for (const [outputKey, outputValue] of Object.entries(sourceNode.outputs)) {
      for (const connection of outputValue.connections)
      if (allLeafNodes.find(n => n.id === connection.node)) {
        connections.push({
          from: sourceNode.id,
          output: outputKey,
          to: connection.node
        });
      }
    }
  }
  return connections;
}

class PasteAction extends Action {
  constructor(editor, pastePayload, mouse) {
    super();
    this.editor = editor;
    this.pastePayload = pastePayload;
    this.mouse = mouse;
  }

  undo() {
    const nodesCount = this.pastePayload.leafNodes.length;
    const connections = getConnectionsToPaste(this.pastePayload);
    const connectionsCount = connections.length;
    for (let i=0; i < nodesCount + connectionsCount; i++) {
      this.editor.trigger('undo')
    }
  
  }

  async redo() {
    await pasteNodesFromClipboard(this.editor, this.pastePayload, this.mouse, true);
    // Because pasting is asynchronous, when a paste action is redone
    // it is added before the node/connection creation actions
    // (e.g. we get [...previousHistory, pasteAction, addNodeAction, addNodeAction, createAction])
    // We want the paste action to be at the end of the history stack
    // so that undoing it again will undo the whole paste operation
    const historyStack = this.editor.history.produced
    const pasteAction = findLast(historyStack, action => action instanceof PasteAction);
    const pasteActionIndex = historyStack.indexOf(pasteAction);
    historyStack.splice(pasteActionIndex, 1);
    historyStack.push(pasteAction);
  }
}

